import { Outlet, useLocation, useRouter } from '@tanstack/react-router';
import type { AnchorHTMLAttributes, ReactNode } from 'react';

/**
 * `react-router`'s surface, answered by the router this panel actually uses.
 *
 * ⚠️ **Fifteen of the template's files import `react-router`.** The panel routes with TanStack
 * Router (§5), and installing a second router would mean two histories fighting over one address
 * bar — the class of bug where the URL says one thing and the screen shows another.
 *
 * ⚠️ **`Link` takes a plain string, deliberately.** TanStack's own `Link` is typed against the
 * route tree, which is the right thing for our screens and the wrong thing here: the template links
 * to `/signin`, `/blank`, `/form-elements` and `#`, and a typed `to` would refuse every one of them
 * at compile time. This navigates through the router — so history, scroll restoration and the
 * back button all behave — while accepting any string, exactly as `react-router` did.
 *
 * ⚠️ A real `<a href>` underneath, not a `div` with a click handler. Middle-click, "open in new
 * tab", and every assistive technology that reads a link as a link all depend on the element.
 */
export function Link({
  to,
  children,
  onClick,
  ...rest
}: { to: string; children?: ReactNode } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const router = useRouter();
  const { pathname } = useLocation();

  return (
    <a
      href={to}
      // ⚠️ **The template marks the current page with a background colour and nothing else.**
      // TanStack's own `Link` sets this attribute, so replacing it with a plain anchor silently
      // removed the only non-visual signal that says which screen you are on — a screen-reader user
      // hears four identical links. Caught by a test that had been asserting it since the panel had
      // three pages.
      aria-current={pathname === to ? 'page' : undefined}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;

        // Let the browser handle anything that is not a plain left click, and anything that is not
        // an in-app path — a modified click is a deliberate request for a new tab or window.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }
        if (!to.startsWith('/')) return;

        event.preventDefault();
        void router.navigate({ to, ignoreBlocker: true } as never);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

/**
 * `useNavigate` with `react-router`'s calling convention: a path, or `-1` for back.
 *
 * TanStack takes an options object and has no numeric form, so both shapes are translated here
 * rather than at each of the template's call sites.
 */
export function useNavigate() {
  const router = useRouter();

  return (to: string | number) => {
    if (typeof to === 'number') {
      router.history.go(to);
      return;
    }
    void router.navigate({ to, ignoreBlocker: true } as never);
  };
}

export { Outlet, useLocation };
